import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listRecordFormOptions, mapApiFormOptionsToUi } from '../../../services/recordFormOptions.js';

const EMPTY = Object.freeze({ locations: [], industries: [], cities: [] });

/**
 * The lists the create form offers. Always arrays, so a select never receives
 * `undefined`.
 *
 * `error` and `reload` exist because the lists are not decoration: a Customer
 * cannot be created without a location, and the only way to choose one is from
 * these options. A failed request has to be reported and retryable, or the user
 * is stuck on a step they cannot complete.
 */
export function useRecordFormOptions() {
  const [options, setOptions] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const { body } = await listRecordFormOptions();
      if (requestId === fetchIdRef.current) setOptions(mapApiFormOptionsToUi(body));
    } catch (err) {
      if (requestId === fetchIdRef.current) setError(err);
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [load]);

  return useMemo(() => ({ options, loading, error, reload: load }), [options, loading, error, load]);
}

export default useRecordFormOptions;
